import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { describeError } from '../../lib/describeError'
import { etDate } from '../labels'
import { AwardOutcome } from './AwardPanel'
import {
  CAMPAIGN_KINDS, SLUG_OK, campaignNeedsChallenge, campaignRunLine, campaigns, challengeEntries, challengeStatusLine,
  challenges, classLabel, countCampaign, createChallenge, deleteChallenge, patchChallenge, pickWinner, rideHref,
  sendCampaign, slugify, zeroCountLine,
  type CampaignDryRun, type CampaignKind, type Challenge, type QueueRow, type WinnerResult,
} from './ridesApi'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const field = `min-h-11 rounded-lg border border-ink-400 bg-ink-800 px-3 font-ui text-[14px] text-paper ${focus}`
const btn = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3 font-ui text-[13px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const btnPrimary = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-3 font-ui text-[13px] font-semibold text-ink-900 hover:opacity-90 disabled:opacity-50 ${focus}`
const lab = 'flex flex-col gap-1 font-ui text-[13px] font-semibold text-paper-dim'

const KEY = (pub: string) => ['rides-challenges', pub]

function ChallengeForm({ pub, initial, onDone }: { pub: string; initial: Challenge | null; onDone: () => void }) {
  const qc = useQueryClient()
  const [theme, setTheme] = useState(initial?.theme ?? '')
  const [slug, setSlug] = useState(initial?.slug ?? '')
  const [slugTouched, setSlugTouched] = useState(false)
  const [blurb, setBlurb] = useState(initial?.blurb ?? '')
  const [opens, setOpens] = useState(initial?.opens_on ?? '')
  const [closes, setCloses] = useState(initial?.closes_on ?? '')
  const [prize, setPrize] = useState(initial?.prize_text ?? '')
  const [issue, setIssue] = useState(initial?.target_issue ?? '')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => initial
      ? patchChallenge(pub, initial.id, { theme, blurb, opens_on: opens, closes_on: closes, prize_text: prize, target_issue: issue })
      : createChallenge(pub, { slug, theme, blurb, opens_on: opens, closes_on: closes, prize_text: prize, target_issue: issue }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: KEY(pub) }); onDone() },
    onError: (e) => setError(describeError(e)),
  })
  const ready = theme.trim() && opens && closes && (initial || SLUG_OK.test(slug))
  return (
    <form role="group" aria-label={initial ? 'Edit challenge' : 'New challenge'} className="flex flex-col gap-3 rounded-xl border border-amber-glow/60 bg-ink-700 p-4"
      onSubmit={(e) => { e.preventDefault(); setError(null); save.mutate() }}>
      <strong>{initial ? `Edit ${initial.theme}` : 'New challenge'}</strong>
      <div className="grid gap-3 md:grid-cols-2">
        <label className={lab}>Theme
          <input className={field} value={theme} maxLength={200} placeholder="Rust & Patina"
            onChange={(e) => { setTheme(e.target.value); if (!initial && !slugTouched) setSlug(slugify(e.target.value)) }} />
        </label>
        <label className={lab}>Web name
          <input className={field} value={slug} disabled={!!initial} maxLength={60} aria-describedby="chal-slug-help"
            onChange={(e) => { setSlugTouched(true); setSlug(e.target.value.toLowerCase()) }} />
          <span id="chal-slug-help" className="text-[12px] font-normal text-paper-faint">
            {initial ? 'The web name cannot change after it is made.' : 'Lowercase letters, numbers and dashes, 3 to 60 characters.'}
          </span>
        </label>
        <label className={lab}>Opens
          <input type="date" className={field} value={opens} onChange={(e) => setOpens(e.target.value)} />
        </label>
        <label className={lab}>Closes
          <input type="date" className={field} value={closes} onChange={(e) => setCloses(e.target.value)} />
        </label>
        <label className={`${lab} md:col-span-2`}>What it is about
          <textarea className={`${field} py-2`} rows={3} value={blurb} maxLength={2000} onChange={(e) => setBlurb(e.target.value)} />
        </label>
        <label className={lab}>Prize
          <input className={field} value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="A year of membership, and the cover story" />
        </label>
        <label className={lab}>Magazine issue
          <input className={field} value={issue} maxLength={64} onChange={(e) => setIssue(e.target.value)} placeholder="2027-01" />
        </label>
      </div>
      {error && <p role="alert" className="m-0 text-[13px] text-radar-red">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" className={btnPrimary} disabled={!ready || save.isPending}>{save.isPending ? 'Saving…' : initial ? 'Save changes' : 'Create challenge'}</button>
        <button type="button" className={btn} disabled={save.isPending} onClick={onDone}>Cancel</button>
      </div>
    </form>
  )
}

function Entries({ pub, path, c, editor }: { pub: string; path: string; c: Challenge; editor: boolean }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['rides-challenge-entries', pub, c.id], queryFn: () => challengeEntries(pub, c.id) })
  const [result, setResult] = useState<WinnerResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<QueueRow | null>(null)
  const pick = useMutation({
    mutationFn: (rideId: number) => pickWinner(pub, c.id, rideId),
    onSuccess: (r) => {
      setError(null); setConfirm(null); setResult(r)
      void qc.invalidateQueries({ queryKey: KEY(pub) }); void qc.invalidateQueries({ queryKey: ['rides-challenge-entries', pub, c.id] })
      void qc.invalidateQueries({ queryKey: ['rides-winners', pub] })
    },
    onError: (e) => { setConfirm(null); setError(describeError(e)) },
  })
  const winnerId = result?.winner_ride_id ?? q.data?.winner_ride_id ?? null
  if (q.isPending) return <p role="status" className="m-0 text-[14px] text-paper-dim">Loading…</p>
  if (q.isError) return <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load the entries. {describeError(q.error)}</p>
  return (
    <div className="flex flex-col gap-3">
      {result && (
        <div role="status" className="flex flex-col gap-3 rounded-xl border border-amber-glow/60 bg-ink-700 p-4">
          <strong>{result.honor}: {q.data.rows.find((r) => r.id === result.winner_ride_id)?.title ?? 'winner picked'}</strong>
          <AwardOutcome pub={pub} rideId={result.winner_ride_id} result={result} onRetried={(r) => setResult({ ...result, ...r })} />
        </div>
      )}
      {error && <p role="alert" className="m-0 text-[13px] text-radar-red">{error}</p>}
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {q.data.rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-ink-500 bg-ink-800 p-2.5">
            {r.cover_url ? <img src={r.cover_url} alt="" className="h-12 w-16 rounded-md object-cover" /> : <div className="h-12 w-16 rounded-md bg-ink-600" />}
            <div className="min-w-0 flex-1">
              <Link to={rideHref(path, r.id)} className={`block font-serif text-[16px] leading-tight text-paper no-underline hover:underline ${focus}`}>{r.title}</Link>
              <span className="font-ui text-[12px] text-paper-faint">{classLabel(r.class_slug)} · {r.status === 'live' ? 'Live' : r.status === 'pending' ? 'To review' : r.status}</span>
            </div>
            {winnerId === r.id && <span className="inline-flex h-6 items-center rounded-full bg-amber-glow/20 px-2.5 font-ui text-[12px] font-semibold text-amber-deep">★ Winner</span>}
            {editor && !winnerId && r.status === 'live' && (
              confirm?.id === r.id ? (
                <span className="inline-flex gap-1.5">
                  <button type="button" className={btnPrimary} disabled={pick.isPending} onClick={() => pick.mutate(r.id)}>{pick.isPending ? 'Awarding…' : 'Award and notify'}</button>
                  <button type="button" className={btn} disabled={pick.isPending} onClick={() => setConfirm(null)}>Cancel</button>
                </span>
              ) : <button type="button" className={btn} aria-label={`Pick ${r.title} as winner`} onClick={() => setConfirm(r)}>Pick winner</button>
            )}
          </li>
        ))}
        {q.data.rows.length === 0 && <li className="font-ui text-[14px] text-paper-dim">No entries yet.</li>}
      </ul>
      {confirm && <p className="m-0 text-[13px] text-paper-dim">Award {confirm.title} the {c.theme} challenge: a year of membership, the winner email and the magazine package.</p>}
      {editor && !winnerId && <p className="m-0 text-[12px] text-paper-faint">Only a live ride can win. Approve an entry first. You can pick before the closing date.</p>}
    </div>
  )
}

function Card({ c, pub, path, editor, onEdit }: { c: Challenge; pub: string; path: string; editor: boolean; onEdit: (c: Challenge) => void }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const del = useMutation({
    mutationFn: () => deleteChallenge(pub, c.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY(pub) }),
    onError: (e) => setError(describeError(e)),
  })
  return (
    <article className="flex flex-col gap-3 rounded-xl border border-ink-500 bg-ink-700 p-4">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="m-0 font-serif text-[22px] font-medium leading-tight">{c.theme}</h2>
          <span className="font-ui text-[12px] text-paper-faint">
            {c.opens_on ? etDate(c.opens_on) : '—'} to {c.closes_on ? etDate(c.closes_on) : '—'}{c.target_issue ? ` · ${c.target_issue} issue` : ''}
          </span>
        </div>
        <span className="inline-flex h-6 items-center rounded-full bg-ink-600 px-2.5 font-ui text-[12px] font-semibold text-paper-dim">{challengeStatusLine(c)}</span>
      </header>
      {c.blurb && <p className="m-0 text-[14px] text-paper-dim">{c.blurb}</p>}
      {c.prize_text && <p className="m-0 text-[13px] text-paper-dim"><b>Prize:</b> {c.prize_text}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btn} aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Hide entries' : `Entries (${c.entry_count})`}</button>
        {editor && <button type="button" className={btn} onClick={() => onEdit(c)}>Edit</button>}
        {editor && c.total_entries === 0 && !c.winner_ride_id && (
          <button type="button" className={btn} disabled={del.isPending} onClick={() => { if (window.confirm(`Delete ${c.theme}?`)) del.mutate() }}>Delete</button>
        )}
      </div>
      {error && <p role="alert" className="m-0 text-[13px] text-radar-red">{error}</p>}
      {open && <Entries pub={pub} path={path} c={c} editor={editor} />}
    </article>
  )
}

const CKEY = (pub: string) => ['rides-campaigns', pub]

function NewsletterBlock({ block }: { block: string | null }) {
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null)
  if (!block) return <p className="m-0 text-[13px] text-paper-dim">No challenge is open, so there is no newsletter block to copy.</p>
  const copy = async () => {
    try { await navigator.clipboard.writeText(block); setCopied('ok') } catch { setCopied('fail') }
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <strong className="text-[14px]">Newsletter block</strong>
        <button type="button" className={btn} onClick={() => void copy()}>Copy the HTML</button>
        <span role="status" className="text-[12px] text-paper-faint">
          {copied === 'ok' ? 'Copied. Paste it into the newsletter as HTML.' : copied === 'fail' ? 'Could not copy. Select the HTML below and copy it.' : ''}
        </span>
      </div>
      <iframe title="Newsletter block preview" srcDoc={block} sandbox="" className="h-64 w-full rounded-lg border border-ink-500 bg-white" />
      <details>
        <summary className="cursor-pointer text-[12px] text-paper-faint">Show the HTML</summary>
        <textarea readOnly aria-label="Newsletter block HTML" className={`${field} mt-2 w-full py-2 font-mono text-[12px]`} rows={5} value={block} onFocus={(e) => e.currentTarget.select()} />
      </details>
    </div>
  )
}

function CampaignsPanel({ pub, rows }: { pub: string; rows: Challenge[] }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: CKEY(pub), queryFn: () => campaigns(pub), refetchInterval: (query) =>
    query.state.data?.runs.some((r) => r.status === 'queued' || r.status === 'running') ? 5000 : false })
  const pickable = rows.filter((c) => c.status !== 'closed')
  const [kind, setKind] = useState<CampaignKind>('challenge_launch')
  const [challengeId, setChallengeId] = useState<number | null>(null)
  const [dry, setDry] = useState<CampaignDryRun | null>(null)
  const [confirm, setConfirm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const needs = campaignNeedsChallenge(kind)
  const chosen = challengeId ?? pickable.find((c) => c.status === 'open')?.id ?? pickable[0]?.id ?? null
  const target = needs ? chosen : null
  const reset = () => { setDry(null); setConfirm(false); setError(null); setDone(null) }
  const count = useMutation({
    mutationFn: () => countCampaign(pub, kind, target),
    onSuccess: (r) => { setError(null); setDry(r) },
    onError: (e) => setError(describeError(e)),
  })
  const send = useMutation({
    mutationFn: () => sendCampaign(pub, kind, target),
    onSuccess: (r) => {
      setConfirm(false); setDry(null); setError(null)
      setDone(`Queued. It goes to ${r.count === 1 ? '1 person' : `${r.count} people`}, about 90 a minute.`)
      void qc.invalidateQueries({ queryKey: CKEY(pub) })
    },
    onError: (e) => { setConfirm(false); setError(describeError(e)) },
  })
  if (q.isPending) return null
  if (q.isError) return <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load the campaign emails. {describeError(q.error)}</p>
  const info = q.data
  const who = CAMPAIGN_KINDS.find((k) => k.key === kind)?.who
  return (
    <section aria-labelledby="rr-campaigns" className="flex flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 p-4">
      <h2 id="rr-campaigns" className="m-0 font-serif text-[22px] font-medium leading-tight">Campaigns</h2>
      {info.can_send ? (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 md:grid-cols-2">
            <label className={lab}>Email
              <select className={field} value={kind} onChange={(e) => { setKind(e.target.value as CampaignKind); reset() }}>
                {CAMPAIGN_KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </label>
            {needs && (
              <label className={lab}>Challenge
                <select className={field} value={chosen ?? ''} onChange={(e) => { setChallengeId(Number(e.target.value) || null); reset() }}>
                  {pickable.length === 0 && <option value="">No open or upcoming challenge</option>}
                  {pickable.map((c) => <option key={c.id} value={c.id}>{c.theme} ({challengeStatusLine(c)})</option>)}
                </select>
              </label>
            )}
          </div>
          {who && <p className="m-0 text-[13px] text-paper-dim">Goes to: {who}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" className={btn} disabled={count.isPending || (needs && !target)} onClick={() => { setDone(null); count.mutate() }}>
              {count.isPending ? 'Counting…' : 'Count who would get it'}
            </button>
            {dry && dry.count > 0 && !confirm && (
              <button type="button" className={btnPrimary} onClick={() => setConfirm(true)}>Send to {dry.count === 1 ? '1 person' : `${dry.count} people`}</button>
            )}
          </div>
          {dry && (
            <div role="status" className="flex flex-col gap-1 text-[13px] text-paper-dim">
              <span><b>Subject:</b> {dry.subject}</span>
              <span>{dry.count === 0 ? zeroCountLine(kind) : `${dry.count === 1 ? '1 person' : `${dry.count} people`} would get it, starting with ${dry.sample.join(', ')}.`}</span>
            </div>
          )}
          {confirm && dry && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-glow/60 bg-ink-800 p-3">
              <span className="text-[13px]">Send “{dry.subject}” to {dry.count === 1 ? '1 person' : `${dry.count} people`} now? This cannot be undone.</span>
              <button type="button" className={btnPrimary} disabled={send.isPending} onClick={() => send.mutate()}>{send.isPending ? 'Queueing…' : 'Yes, send it'}</button>
              <button type="button" className={btn} disabled={send.isPending} onClick={() => setConfirm(false)}>Cancel</button>
            </div>
          )}
          {done && <p role="status" className="m-0 text-[13px] text-paper-dim">{done}</p>}
          {error && <p role="alert" className="m-0 text-[13px] text-radar-red">{error}</p>}
        </div>
      ) : (
        <p className="m-0 text-[13px] text-paper-dim">Only an admin can send campaign emails. You can copy the newsletter block.</p>
      )}
      {info.runs.length > 0 && (
        <ul aria-label="Recent sends" className="m-0 flex list-none flex-col gap-1.5 p-0">
          {info.runs.slice(0, 5).map((r) => (
            <li key={r.id} className="text-[13px] text-paper-dim">
              <b className="text-paper">{CAMPAIGN_KINDS.find((k) => k.key === r.kind)?.label ?? r.kind}</b>
              {r.challenge_theme ? ` · ${r.challenge_theme}` : ''}{r.auto ? ' · automatic' : ''}
              {r.created_at ? ` · ${etDate(r.created_at)}` : ''}: {campaignRunLine(r)}
            </li>
          ))}
        </ul>
      )}
      <NewsletterBlock block={info.newsletter_block} />
    </section>
  )
}

export function ChallengesTab({ pub, path, editor }: { pub: string; path: string; editor: boolean }) {
  const q = useQuery({ queryKey: KEY(pub), queryFn: () => challenges(pub) })
  const [form, setForm] = useState<{ edit: Challenge | null } | null>(null)
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="m-0 text-[14px] text-paper-dim">One build challenge a month. Readers enter while it is open; at the close you pick a winner.</p>
        {editor && !form && <button type="button" className={btnPrimary} onClick={() => setForm({ edit: null })}>New challenge</button>}
      </div>
      {form && <ChallengeForm key={form.edit?.id ?? 'new'} pub={pub} initial={form.edit} onDone={() => setForm(null)} />}
      {q.data && <CampaignsPanel pub={pub} rows={q.data.rows} />}
      {q.isPending && <p role="status" className="m-0 text-[14px] text-paper-dim">Loading…</p>}
      {q.isError && <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load the challenges. {describeError(q.error)}</p>}
      {q.data && (
        <div className="grid gap-4 md:grid-cols-2">
          {q.data.rows.map((c) => <Card key={c.id} c={c} pub={pub} path={path} editor={editor} onEdit={(x) => setForm({ edit: x })} />)}
          {q.data.rows.length === 0 && <p className="m-0 text-[14px] text-paper-dim">No challenges yet.</p>}
        </div>
      )}
    </div>
  )
}
